<script setup lang="ts">
import type { Theme } from "@/types/config/theme";
import type { Config } from "@/types/config";

import { onBeforeMount, computed, shallowRef, onBeforeUnmount } from "vue";
import { createHeightObserver, isInIFrame } from "@embed/lib/iframe";
import { jwtConfigToConfig } from "@embed/lib/config";
import { isValidHostname } from "@embed/lib/hostname";
import { clearOldMapCaches } from "@embed/lib/fetch";
import { DEFAULT_CONFIG } from "@/types/config";
import { applyTheme } from "@embed/lib/theme";
import { isDev } from "@/lib/env";

import NoRacesProvided from "@embed/components/alerts/NoRacesProvided.vue";
import InvalidHostname from "@embed/components/alerts/InvalidHostname.vue";
import MissingConfig from "@embed/components/alerts/MissingConfig.vue";
import InvalidConfig from "@embed/components/alerts/InvalidConfig.vue";
import LoadingEmbed from "@embed/components/spinners/LoadingEmbed.vue";
import EmbedWrapper from "@embed/components/EmbedWrapper.vue";

let clearMapCache: ReturnType<typeof setTimeout>;
let cleanupResizeObserver: ResizeObserver["disconnect"];

const { hostname } = window.location;
const loading: ShallowRef<boolean> = shallowRef(true);
const search = new URLSearchParams(window.location.search);
const embedID = search.get("id");
const rawConfig = search.get("c");
const config: ShallowRef<Config | undefined> = shallowRef(undefined);

const isInvalidConfig = computed(() => {
  return loading.value === false && config.value === undefined;
});

const isInvalidHostname = computed(() => {
  return (
    config.value !== undefined &&
    !isValidHostname(hostname, config.value.theme.toLowerCase() as Theme)
  );
});

const noRaces = computed(() => {
  if (config.value === undefined) return false;

  const noBalance = !("balance" in config.value);
  const noRaceIDs = !("raceIDs" in config.value) || config.value.raceIDs.length === 0;
  const noRacesBlobPath =
    !("racesBlobPath" in config.value) || config.value.racesBlobPath.length === 0;

  return noBalance && noRaceIDs && noRacesBlobPath;
});

function setupTheme(theme: Theme) {
  if (theme !== DEFAULT_CONFIG.theme) {
    applyTheme(theme);
  }

  if (theme === "ddhq-dark") {
    document.documentElement.classList.add("dark");

    if (isDev) {
      document.body.classList.add("bg-black");
    }
  }
}

onBeforeMount(async () => {
  if (isInIFrame() && embedID !== null) {
    cleanupResizeObserver = createHeightObserver(embedID).disconnect;
  } else {
    document.documentElement.style.overflowY = "auto";
    document.body.style.overflowY = "auto";
  }

  if (rawConfig) {
    config.value = await jwtConfigToConfig(rawConfig);

    setTimeout(clearOldMapCaches, 3_000);

    loading.value = false;

    if (config.value) {
      setupTheme(config.value.theme);
    }
  }
});

onBeforeUnmount(() => {
  if (cleanupResizeObserver) {
    cleanupResizeObserver();

    if (clearMapCache) {
      clearTimeout(clearMapCache);
    }
  }
});
</script>

<template>
  <MissingConfig
    v-if="rawConfig === null"
    class="mt-8" />

  <InvalidConfig
    v-else-if="isInvalidConfig"
    class="mt-8" />

  <InvalidHostname
    v-else-if="isInvalidHostname"
    class="mt-8"
    :hostname="hostname" />

  <NoRacesProvided
    v-else-if="noRaces"
    class="mt-8" />

  <Suspense v-else-if="config">
    <EmbedWrapper
      :config="config"
      :raw-config="rawConfig ?? undefined" />

    <template #fallback>
      <LoadingEmbed />
    </template>
  </Suspense>

  <LoadingEmbed
    v-else
    class="mt-8" />
</template>
